import { CustomCSS, imageField, urlField, textField } from "../templates/CustomCSS.ts";
import { DeclarativeSource } from "../DeclarativeSource.ts";
import { resolveUrl } from "../UrlResolver.ts";
import { getSourcePolicy } from "../../network/SourceRegistry.ts";
import type { SourceProfile } from "../types.ts";
import type { YomiriHttpClient } from "../../network/types.ts";
import type { YomiriPage } from "../../types/extension.ts";
import { NetworkError } from "../../network/types.ts";

export function DbNewHopeProfile(): SourceProfile {
  return CustomCSS({
    schemaVersion: 1,
    id: "dbnewhope",
    name: "DB New Hope",
    baseUrl: "https://dbnewhope.com",
    allowedHosts: [...getSourcePolicy("dbnewhope").allowedHosts],
    template: "CustomCSS",
    corsOpen: false,
    ui: {
      idMode: "url",
      mangaPath: "/espanol/",
      chapterPath: "/espanol/",
      trailingSlash: false,
      languages: ["es"],
      iconUrl: "/source-icons/dbnewhope.png",
    },
    catalog: {
      steps: [
        {
          url: "{baseUrl}/espanol.php",
          extract: {
            kind: "html",
            selector: "header.masthead",
            fields: {
              title: textField("h1.masthead-heading"),
              url: urlField('a[href*="capitulos.php"]'),
              coverUrl: imageField("img.masthead-avatar", false),
            },
          },
        },
      ],
    },
    operations: {
      search: {
        steps: [
          {
            url: "{baseUrl}/espanol.php",
            extract: {
              kind: "html",
              selector: "header.masthead",
              fields: {
                title: textField("h1.masthead-heading"),
                url: urlField('a[href*="capitulos.php"]'),
                coverUrl: imageField("img.masthead-avatar", false),
              },
            },
          },
        ],
      },
      detail: {
        steps: [
          {
            url: "{baseUrl}/espanol.php",
            extract: {
              kind: "html",
              fields: {
                title: textField("h1.masthead-heading"),
                coverUrl: imageField("img.masthead-avatar", false),
                description: textField("header p.masthead-subheading", false),
              },
            },
          },
        ],
      },
      chapters: {
        steps: [
          {
            url: "{baseUrl}/espanol/capitulos.php",
            extract: {
              kind: "html",
              selector: ".card",
              fields: {
                name: textField(".card-title"),
                url: urlField("a[href]"),
              },
            },
          },
        ],
      },
      pages: {
        steps: [
          {
            url: "{chapter.url}",
            referer: "{baseUrl}/",
            extract: {
              kind: "html",
              selector: "img.manga-page",
              fields: {
                url: imageField(),
              },
            },
          },
        ],
      },
    },
  });
}

export class DbNewHope extends DeclarativeSource {
  constructor(
    profile: unknown,
    private readonly client: Pick<YomiriHttpClient, "text"> &
      Partial<Pick<YomiriHttpClient, "prepareImage">>,
  ) {
    super(profile, client);
  }

  override async getPages(id: string): Promise<YomiriPage[]> {
    const chapterUrl = this.resourceUrl(id, "chapter");
    const firstResponse = await this.client.text({
      sourceId: this.id,
      url: chapterUrl,
      method: "GET",
      responseType: "text",
      credentials: "omit",
      cache: { ttlMs: 300000, scope: "public" },
      headers: { Referer: `${this.profile.baseUrl}/` },
    });

    const parser = new DOMParser();
    const firstDoc = parser.parseFromString(firstResponse.body, "text/html");
    const firstImg = firstDoc.querySelector("img.manga-page");
    const firstSrc =
      firstImg?.getAttribute("src") ||
      firstImg?.getAttribute("data-src") ||
      firstImg?.getAttribute("data-original");

    if (!firstSrc) {
      throw new Error("No se encontró la imagen de la página en DB New Hope.");
    }

    const firstAbsolute = resolveUrl(firstSrc, firstResponse.finalUrl, this.profile);
    const options = Array.from(firstDoc.querySelectorAll(".nav select option, select option"));
    const pageCount = options.length;
    if (pageCount > 500) throw new NetworkError("INVALID_RESPONSE", "Cantidad de páginas inválida.");

    if (pageCount <= 1) {
      return [
        {
          index: 0,
          imageUrl: firstAbsolute,
          sourceId: this.id,
          headers: { Referer: `${this.profile.baseUrl}/` },
        },
      ];
    }

    const cleanBaseUrl = chapterUrl.replace(/\?.*$/, "");
    const pageUrls: string[] = new Array(pageCount);
    pageUrls[0] = firstAbsolute;

    const pageNumbers: number[] = [];
    for (let p = 2; p <= pageCount; p++) {
      pageNumbers.push(p);
    }

    let nextIdx = 0;
    let failedPage: { page: number; cause: unknown } | undefined;
    const concurrency = Math.min(8, pageNumbers.length);
    const worker = async () => {
      while (nextIdx < pageNumbers.length) {
        const itemIdx = nextIdx++;
        const pageNum = pageNumbers[itemIdx];
        try {
          const pageRes = await this.client.text({
            sourceId: this.id,
            url: `${cleanBaseUrl}?page=${pageNum}`,
            method: "GET",
            responseType: "text",
            credentials: "omit",
            cache: { ttlMs: 300000, scope: "public" },
            headers: { Referer: chapterUrl },
          });
          const doc = parser.parseFromString(pageRes.body, "text/html");
          const img = doc.querySelector("img.manga-page");
          const src =
            img?.getAttribute("src") ||
            img?.getAttribute("data-src") ||
            img?.getAttribute("data-original");
          if (!src || pageRes.status >= 400 || pageRes.classification === "challenge")
            throw new Error(`No se pudo extraer la página ${pageNum}.`);
          pageUrls[pageNum - 1] = resolveUrl(src, pageRes.finalUrl, this.profile);
        } catch (cause) {
          failedPage ??= { page: pageNum, cause };
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    if (failedPage)
      throw new NetworkError("INVALID_RESPONSE", `DB New Hope: no se pudo cargar la página ${failedPage.page}. Reintentá el capítulo.`);

    return pageUrls
      .filter((url): url is string => Boolean(url))
      .map((imageUrl, index) => ({
        index,
        imageUrl,
        sourceId: this.id,
        headers: { Referer: `${this.profile.baseUrl}/` },
      }));
  }
}
